import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectsApi, Project } from '../../api/projectsApi';
import { taskTypesApi, TaskType } from '../../api/taskTypesApi';
import { timesheetsApi, Timesheet, WeeklyViewResponse } from '../../api/timesheetsApi';
import { projectRequestsApi, ProjectRequest } from '../../api/projectRequestsApi';
import { settingsApi, SystemSetting } from '../../api/settingsApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import { formatDate, formatDateTime } from '../../utils/helpers';
import { STATUS_COLORS } from '../../utils/constants';
import { ProjectRequestModal } from '../Dashboard/EmployeeDashboard/ProjectRequestModal';
import './LogTime.css';

export function LogTime() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  // Form state
  const [projectId, setProjectId] = useState<number | ''>('');
  const [taskTypeId, setTaskTypeId] = useState<number | ''>('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState<number | ''>('');
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [description, setDescription] = useState('');
  
  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [weeklyData, setWeeklyData] = useState<WeeklyViewResponse | null>(null);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Selected date for navigation (defaults to today)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Project request state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [myRequests, setMyRequests] = useState<ProjectRequest[]>([]);

  // Settings state
  // Note: maxHoursPerDay is used for dropdown suggestions only, not as a hard limit
  // Users can exceed this value. The 0.5 minimum is hardcoded for validation
  // For email notifications about unfulfilled hours, use maxHoursPerDay as the daily target
  const [maxHoursPerDay, setMaxHoursPerDay] = useState<number>(8);
  const [minHoursPerEntry, setMinHoursPerEntry] = useState<number>(0.5);
  const [hourIncrements, setHourIncrements] = useState<number>(0.5);

  // Track previous week to avoid unnecessary refetches
  const previousWeekStartRef = useRef<string | null>(null);
  const settingsLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    fetchInitialData();
  }, [selectedDate]);

  // Fetch project requests
  const fetchMyRequests = async () => {
    try {
      const response = await projectRequestsApi.getMyRequests();
      setMyRequests(response.requests);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const handleRequestSuccess = () => {
    fetchMyRequests();
    fetchInitialData();
  };

  // Load timesheet data when editing
  useEffect(() => {
    if (editId && projects.length > 0 && taskTypes.length > 0) {
      loadTimesheetForEdit(parseInt(editId));
    } else if (!editId) {
      // Clear edit mode when no edit param
      setIsEditMode(false);
      setEditingTimesheet(null);
    }
  }, [editId, projects, taskTypes]);

  const loadTimesheetForEdit = async (timesheetId: number) => {
    try {
      const response = await timesheetsApi.getTimesheet(timesheetId);
      const timesheet = response.timesheet;
      
      // Pre-fill form with existing data
      setProjectId(timesheet.project_id);
      setTaskTypeId(timesheet.task_type_id);
      setDate(timesheet.date);
      setSelectedDate(timesheet.date);
      setHours(timesheet.hours);
      setDescription(timesheet.description || '');
      setEditingTimesheet(timesheet);
      setIsEditMode(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load timesheet');
      // Clear edit params on error
      navigate('/log-time', { replace: true });
    }
  };

  const getWeekStartForDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);
    return monday.toISOString().split('T')[0];
  };

  const fetchInitialData = async () => {
    try {
      const weekStart = getWeekStartForDate(selectedDate);
      
      // Check if we're still in the same week - only fetch weekly data if week changed
      const isSameWeek = previousWeekStartRef.current === weekStart;
      
      if (!isSameWeek) {
        setLoading(true);
      }
      setError(null);
      
      // Fetch projects and tasks on first load
      if (!projects.length) {
        const projectsRes = await projectsApi.getMyProjects();
        setProjects(projectsRes.projects);
      }
      if (!taskTypes.length) {
        const tasksRes = await taskTypesApi.getTaskTypes();
        setTaskTypes(tasksRes.tasks.filter(t => !t.is_leave_type));
      }
      
      // Fetch weekly data if week changed or first load
      if (!isSameWeek) {
        const weeklyRes = await timesheetsApi.getWeeklyView(weekStart);
        setWeeklyData(weeklyRes);
        previousWeekStartRef.current = weekStart;
      }

      // Fetch settings only once
      if (!settingsLoadedRef.current) {
        try {
          const settingsRes = await settingsApi.getSettings();
          const settings = settingsRes.settings;
          
          const maxHoursSetting = settings.find(s => s.key === 'max_hours_per_day');
          const minHoursSetting = settings.find(s => s.key === 'min_hours_per_entry');
          const incrementSetting = settings.find(s => s.key === 'hour_increments');
          
          if (maxHoursSetting) setMaxHoursPerDay(Number(maxHoursSetting.value));
          if (minHoursSetting) setMinHoursPerEntry(Number(minHoursSetting.value));
          if (incrementSetting) setHourIncrements(Number(incrementSetting.value));
          
          settingsLoadedRef.current = true;
        } catch (settingsErr) {
          console.warn('Failed to load settings, using defaults');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectId || !taskTypeId || !date || !hours) {
      setError('Please fill in all required fields');
      return;
    }

    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    // Fixed minimum hours validation (not dynamic)
    if (hours < 0.5) {
      setError('Hours must be at least 0.5');
      return;
    }

    // Validate date is not in the future
    const today = new Date().toISOString().split('T')[0];
    if (date > today) {
      setError('Cannot create timesheet for future dates');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      
      if (isEditMode && editingTimesheet) {
        // Update existing timesheet
        await timesheetsApi.updateTimesheet(editingTimesheet.id, {
          task_type_id: taskTypeId as number,
          hours: hours as number,
          description: description.trim() || undefined,
        });
        
        setSuccessMessage('Timesheet updated successfully!');
        
        // Clear edit mode and redirect
        setTimeout(() => {
          navigate('/timesheets');
        }, 1500);
      } else {
        // Create new timesheet
        await timesheetsApi.createTimesheet({
          project_id: projectId as number,
          task_type_id: taskTypeId as number,
          date,
          hours: hours as number,
          description: description.trim() || undefined,
        });
        
        setSuccessMessage('Timesheet submitted successfully!');
        
        // Reset form
        setProjectId('');
        setTaskTypeId('');
        setHours('');
        setDescription('');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      }
      
      // Refresh weekly data
      const weekStart = getWeekStartForDate(selectedDate);
      const weeklyRes = await timesheetsApi.getWeeklyView(weekStart);
      setWeeklyData(weeklyRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingTimesheet(null);
    setProjectId('');
    setTaskTypeId('');
    const today = new Date().toISOString().split('T')[0];
    setDate(today);
    setSelectedDate(today);
    setHours('');
    setDescription('');
    navigate('/log-time', { replace: true });
  };

  const loadTimesheetForDate = (dateStr: string) => {
    // Always show fresh form fields when navigating to a date.
    // This allows users to add multiple entries for different projects on the same day.
    // Users can edit existing entries from the "Recent Entries" table below.
    setProjectId('');
    setTaskTypeId('');
    setHours('');
    setDescription('');
  };

  const getWeekDates = () => {
    if (!weeklyData) return [];
    
    const dates = [];
    const start = new Date(weeklyData.week_start);
    
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNum = d.getDate();
      const today = new Date().toISOString().split('T')[0];
      const isToday = dateStr === today;
      const isWeekend = i >= 5;
      const isFuture = dateStr > today;
      
      dates.push({
        dateStr,
        dayName,
        dayNum,
        isToday,
        isWeekend,
        isFuture,
        isSelected: dateStr === selectedDate,
        hours: weeklyData.daily_totals[dateStr] || 0,
        entries: weeklyData.entries[dateStr] || [],
      });
    }
    
    return dates;
  };

  if (loading && !weeklyData) {
    return null; // Quick initial load without animation
  }

  // Generate hours options for dropdown suggestions
  // Note: maxHoursPerDay is used for dropdown options only, not validation
  // Users can manually enter values beyond this limit
  // Validation only enforces minimum of 0.5 hours
  const hoursOptions = [];
  for (let h = minHoursPerEntry; h <= maxHoursPerDay; h += hourIncrements) {
    hoursOptions.push(Number(h.toFixed(2)));
  }

  const weekDates = getWeekDates();

  return (
    <div className="log-time-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Fill Timesheet</h1>
            <p>Record your daily work hours</p>
          </div>
          <div className="header-actions">
            <Button
              variant="secondary"
              onClick={() => setShowRequestModal(true)}
            >
              <Icons.Plus />
              Request Project
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/timesheets')}
            >
              <Icons.History />
              View All Timesheets
            </Button>
          </div>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            <Icons.X />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <Icons.CheckCircle />
          <span>{successMessage}</span>
        </div>
      )}

      <div className="log-time-grid">
        {/* Time Entry Form */}
        <Card className="entry-form-card">
          <div className="card-header-title">
            <Icons.Clock />
            <span>{isEditMode ? 'Edit Time Entry' : 'New Time Entry'}</span>
          </div>
          
          {projects.length === 0 ? (
            <div className="no-projects-message">
              <Icons.Folder />
              <h3>No Projects Assigned</h3>
              <p>You need to be assigned to a project before you can log time.</p>
              <Button
                variant="primary"
                onClick={() => setShowRequestModal(true)}
              >
                Request Project Assignment
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="project">
                  Project <span className="required">*</span>
                </label>
                <div className="select-wrapper">
                  <select
                    id="project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : '')}
                    required
                    disabled={submitting || isEditMode}
                    className="form-select"
                  >
                    <option value="">Select a project</option>
                    {projects
                      .filter((project) => project.code !== 'LEAVE')
                      .map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} {project.client_name ? `(${project.client_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="taskType">
                  Task <span className="required">*</span>
                </label>
                <div className="select-wrapper">
                  <select
                    id="taskType"
                    value={taskTypeId}
                    onChange={(e) => setTaskTypeId(e.target.value ? Number(e.target.value) : '')}
                    required
                    disabled={submitting}
                    className="form-select"
                  >
                    <option value="">Select a task</option>
                    {taskTypes.map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date">
                    Date <span className="required">*</span>
                  </label>
                  <div className="date-input-wrapper">
                    <input
                      ref={dateInputRef}
                      type="date"
                      id="date"
                      value={date}
                      onChange={(e) => {
                        setDate(e.target.value);
                        setSelectedDate(e.target.value);
                        setDatePickerOpen(false);
                      }}
                      onBlur={() => setDatePickerOpen(false)}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      disabled={submitting || isEditMode}
                      className="form-input date-input-no-indicator"
                    />
                    {!isEditMode && !submitting && (
                      <button
                        type="button"
                        className="date-picker-toggle"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (datePickerOpen) {
                            dateInputRef.current?.blur();
                            setDatePickerOpen(false);
                          } else {
                            dateInputRef.current?.showPicker?.();
                            dateInputRef.current?.focus();
                            setDatePickerOpen(true);
                          }
                        }}
                        tabIndex={-1}
                      >
                        <Icons.Calendar />
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="hours">
                    Hours <span className="required">*</span>
                  </label>
                  <div className="select-wrapper">
                    <input
                      type="number"
                      id="hours"
                      value={hours}
                      onChange={(e) => setHours(e.target.value ? Number(e.target.value) : '')}
                      min={0.5}
                      step={hourIncrements}
                      placeholder="Select or enter hours"
                      required
                      disabled={submitting}
                      className="form-input hours-input"
                      list="hours-datalist"
                    />
                  </div>
                  <datalist id="hours-datalist">
                    {hoursOptions.map((h) => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="form-group">
  <label htmlFor="description">
    Description <span className="required">*</span>
  </label>

  <textarea
    id="description"
    value={description}
    onChange={(e) => setDescription(e.target.value)}
    placeholder="What did you work on?"
    rows={3}
    disabled={submitting}
    className="form-textarea"
  />

  
  {error === 'Description is required' && (
    <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
      {error}
    </p>
  )}
</div>

              <div className="form-actions">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancelEdit}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || !projectId || !taskTypeId || !date || !hours || !description.trim()}
                >
                  {submitting 
                    ? (isEditMode ? 'Updating...' : 'Submitting...') 
                    : (isEditMode ? 'Update Entry' : 'Submit Entry')}
                </Button>
              </div>
            </form>
          )}
        </Card>

        {/* Weekly Overview */}
        <Card className="weekly-overview-card">
          <div className="card-header-title week-nav-header">
                        <button 
              className="week-nav-btn" 
              onClick={() => {
                const prevDate = new Date(selectedDate);
                prevDate.setDate(prevDate.getDate() - 1);
                const prevDateStr = prevDate.toISOString().split('T')[0];
                setSelectedDate(prevDateStr);
                setDate(prevDateStr);
                loadTimesheetForDate(prevDateStr);
              }}
              title="Previous day"
            >
              <Icons.ChevronLeft />
            </button>
            <div className="week-header-center">
              <Icons.Calendar />
              <span>This Week Overview</span>
            </div>
                        <button 
              className="week-nav-btn" 
              onClick={() => {
                const nextDate = new Date(selectedDate);
                nextDate.setDate(nextDate.getDate() + 1);
                const nextDateStr = nextDate.toISOString().split('T')[0];
                setSelectedDate(nextDateStr);
                setDate(nextDateStr);
                loadTimesheetForDate(nextDateStr);
              }}
              disabled={selectedDate >= new Date().toISOString().split('T')[0]}
              title="Next day"
            >
              <Icons.ChevronRight />
            </button>
            {weeklyData && (
              <span className="week-total">
                Total: {weeklyData.week_total}h / 40h
              </span>
            )}
          </div>

          <div className="week-grid">
            {weekDates.map((day) => (
              <div
                key={day.dateStr}
                className={`day-cell ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''} ${day.isWeekend ? 'weekend' : ''} ${day.isFuture ? 'future disabled' : ''}`}
                onClick={() => {
                  if (!day.isFuture) {
                    setSelectedDate(day.dateStr);
                    setDate(day.dateStr);
                    loadTimesheetForDate(day.dateStr);
                  }
                }}
                style={{ cursor: day.isFuture ? 'not-allowed' : 'pointer', opacity: day.isFuture ? 0.5 : 1 }}
              >
                <div className="day-header">
                  <span className="day-name">{day.dayName}</span>
                  <span className="day-num">{day.dayNum}</span>
                </div>
                <div className="day-hours">
                  <span className={`hours-value ${day.hours >= 8 ? 'full' : day.hours > 0 ? 'partial' : ''}`}>
                    {day.hours}h
                  </span>
                </div>
                {day.entries.length > 0 && (
                  <div className="day-entries">
                    {day.entries.slice(0, 2).map((entry) => (
                      <div
                        key={entry.id}
                        className="entry-chip"
                        style={{
                          backgroundColor: `${STATUS_COLORS[entry.status]}15`,
                          color: STATUS_COLORS[entry.status],
                        }}
                      >
                        {entry.project_name?.substring(0, 8)}...
                      </div>
                    ))}
                    {day.entries.length > 2 && (
                      <span className="more-entries">+{day.entries.length - 2}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Timestamps for selected date entries */}
          {weeklyData && weeklyData.entries[selectedDate] && weeklyData.entries[selectedDate].length > 0 && (
            <div className="selected-date-timestamps">
              <div className="timestamps-header">
                <Icons.Clock />
                <span>Entry Timestamps for {formatDate(selectedDate)}</span>
              </div>
              <div className="timestamps-list">
                {weeklyData.entries[selectedDate].map((entry) => (
                  <div key={entry.id} className="timestamp-entry">
                    <span className="timestamp-project">{entry.project_name} — {entry.task_type_name}</span>
                    <div className="timestamp-values">
                      <span className="timestamp-item">
                        <strong>Created:</strong> {entry.created_at ? formatDateTime(entry.created_at) : '—'}
                      </span>
                      <span className="timestamp-item">
                        <strong>Updated:</strong> {entry.updated_at ? formatDateTime(entry.updated_at) : '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Recent Entries */}
      {weeklyData && Object.keys(weeklyData.entries).length > 0 && (
        <Card className="recent-entries-card">
          <div className="card-header-title">
            <Icons.History />
            <span>Recent Entries This Week</span>
          </div>

          <div className="entries-table-container">
            <table className="entries-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Task</th>
                  <th>Hours</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(weeklyData.entries)
                  .flatMap(([_, entries]) => entries)
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 10)
                  .map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.date)}</td>
                      <td>{entry.project_name}</td>
                      <td>{entry.task_type_name}</td>
                      <td><strong>{entry.hours}h</strong></td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: `${STATUS_COLORS[entry.status]}15`,
                            color: STATUS_COLORS[entry.status],
                          }}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="description-cell">
                        {entry.description || '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Project Request Modal */}
      <ProjectRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={handleRequestSuccess}
      />
    </div>
  );
}

export default LogTime;
